import React, { useState, useEffect } from 'react';
import { getAuth, confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { app } from '../firebase/authConfig';
import { REGION_FULL } from '../constants';

const P = {
  red:'#C62828', redD:'#B71C1C', redL:'#FFEBEE', redB:'#FFCDD2',
  cream:'#FFFAF5', beige:'#F1E6D8', beigeD:'#EDE4D8', beigeB:'#DDD0C0',
  white:'#FFFFFF', border:'#EAE3DA',
  text:'#1A0E06', sub:'#4A3728', muted:'#6B4F3A',
  font:"'DM Sans', 'Segoe UI', sans-serif",
  fontSerif:"'DM Serif Display', Georgia, serif",
  green:'#1B5E20', greenL:'#E8F5E9', greenB:'#A5D6A7',
};

export default function ResetPasswordPage() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [invalidCode, setInvalidCode] = useState(false);

  const navigate = useNavigate();
  const auth = getAuth(app);

  const params = new URLSearchParams(window.location.search);
  const oobCode = params.get('oobCode');
  const mode = params.get('mode');

  useEffect(() => {
    if (!oobCode || mode !== 'resetPassword') {
      setInvalidCode(true);
      setVerifying(false);
      return;
    }
    verifyPasswordResetCode(auth, oobCode)
      .then(resolvedEmail => {
        setEmail(resolvedEmail);
        setVerifying(false);
      })
      .catch(() => {
        setInvalidCode(true);
        setVerifying(false);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSubmit(e) {
    e.preventDefault();
    if (password.length < 6) {
      setError('Ang password ay dapat hindi bababa sa 6 na karakter.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Hindi magkaparehong password. Subukan muli.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setDone(true);
    } catch (err) {
      if (err.code === 'auth/expired-action-code') {
        setError('Nag-expire na ang reset link. Bumalik at humiling ng bago.');
      } else if (err.code === 'auth/invalid-action-code') {
        setError('Hindi valid ang link na ito. Maaaring nagamit na o nag-expire. Humiling ng bagong reset link.');
      } else if (err.code === 'auth/weak-password') {
        setError('Masyadong mahina ang password. Gamitin ang mas mahaba o mas kumplikadong password.');
      } else {
        setError('May error. Subukang muli.');
      }
    } finally {
      setLoading(false);
    }
  }

  const inp = {
    width: '100%',
    padding: '12px 44px 12px 14px',
    border: `1.5px solid ${P.beigeB}`,
    fontSize: 13, color: P.text, outline: 'none',
    background: P.beige, boxSizing: 'border-box',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    fontFamily: P.font,
  };

  return (
    <div style={{ minHeight: '100vh', fontFamily: P.font, position: 'relative', overflow: 'hidden', background: P.beige }}>

      {/* Background */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 0, backgroundImage: `linear-gradient(135deg, ${P.redD} 0%, #8B1A1A 40%, #3D0A0A 100%)` }}>
        <svg style={{ position: 'absolute', bottom: 0, left: 0, right: 0, width: '100%', opacity: 0.12 }} viewBox="0 0 1440 320" preserveAspectRatio="none">
          <path fill="#fff" d="M0,160L48,144C96,128,192,96,288,106.7C384,117,480,171,576,181.3C672,192,768,160,864,138.7C960,117,1056,107,1152,117.3C1248,128,1344,160,1392,176L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"/>
        </svg>
        <svg style={{ position: 'absolute', bottom: 0, left: 0, right: 0, width: '100%', opacity: 0.08 }} viewBox="0 0 1440 320" preserveAspectRatio="none">
          <path fill="#fff" d="M0,256L60,240C120,224,240,192,360,186.7C480,181,600,203,720,213.3C840,224,960,224,1080,208C1200,192,1320,160,1380,144L1440,128L1440,320L1380,320C1320,320,1200,320,1080,320C960,320,840,320,720,320C600,320,480,320,360,320C240,320,120,320,60,320L0,320Z"/>
        </svg>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 39px, rgba(255,255,255,0.04) 40px), repeating-linear-gradient(90deg, transparent, transparent 39px, rgba(255,255,255,0.04) 40px)` }}/>
      </div>

      {/* Content */}
      <div style={{ position: 'relative', zIndex: 1, minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' }}>

        {/* Brand */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 38, fontWeight: 900, color: '#fff', letterSpacing: '0.1em', lineHeight: 1 }}>F.L.A.R.E.</div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginTop: 8, fontWeight: 500, letterSpacing: '0.04em' }}>
            Flood Level Alert and Risk Evaluation System
          </div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 4, fontWeight: 400 }}>{REGION_FULL}</div>
        </div>

        {/* Card */}
        <div style={{ width: '100%', maxWidth: 420, background: P.white, boxShadow: '0 32px 80px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.1)', overflow: 'hidden', animation: 'slideUp 0.4s cubic-bezier(0.34,1.1,0.64,1)' }}>

          {/* Card header */}
          <div style={{ background: P.red, padding: '18px 28px', borderBottom: `3px solid ${P.redD}` }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#fff', lineHeight: 1 }}>Baguhin ang Password</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.72)', marginTop: 3, fontWeight: 500 }}>
              {verifying ? 'Bine-verify ang reset link...' : invalidCode ? 'Hindi valid ang link' : `Para sa account: ${email}`}
            </div>
          </div>

          {/* Card body */}
          <div style={{ padding: '32px 28px 28px' }}>

            {verifying ? (
              <div style={{ textAlign: 'center', padding: '24px 0', color: P.muted, fontSize: 13, fontWeight: 500 }}>
                <div style={{ marginBottom: 8, fontSize: 22 }}>⏳</div>
                Sinisigurado ang link...
              </div>
            ) : invalidCode ? (
              <div>
                <div style={{ background: P.redL, border: `1px solid ${P.redB}`, padding: '16px 14px', marginBottom: 24 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: P.redD, marginBottom: 4 }}>Hindi valid ang reset link</div>
                  <div style={{ fontSize: 12, color: P.sub, lineHeight: 1.6 }}>
                    Maaaring nag-expire na o nagamit na ang link na ito. Humiling ng bagong reset link.
                  </div>
                </div>
                <button
                  onClick={() => navigate('/forgot-password')}
                  style={{ width: '100%', background: P.red, color: '#fff', border: 'none', padding: '13px 18px', fontSize: 13, fontWeight: 800, cursor: 'pointer', letterSpacing: '0.04em', fontFamily: P.font }}
                  onMouseEnter={e => { e.currentTarget.style.background = P.redD; }}
                  onMouseLeave={e => { e.currentTarget.style.background = P.red; }}
                >
                  Humiling ng Bagong Reset Link
                </button>
              </div>
            ) : done ? (
              <div>
                <div style={{ background: P.greenL, border: `1px solid ${P.greenB}`, padding: '16px 14px', marginBottom: 24 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: P.green, marginBottom: 4 }}>
                    Matagumpay na na-reset ang password!
                  </div>
                  <div style={{ fontSize: 12, color: P.sub, lineHeight: 1.6 }}>
                    Maaari ka nang mag-login gamit ang iyong bagong password.
                  </div>
                </div>
                <button
                  onClick={() => navigate('/login')}
                  style={{ width: '100%', background: P.red, color: '#fff', border: 'none', padding: '13px 18px', fontSize: 13, fontWeight: 800, cursor: 'pointer', letterSpacing: '0.04em', fontFamily: P.font }}
                  onMouseEnter={e => { e.currentTarget.style.background = P.redD; }}
                  onMouseLeave={e => { e.currentTarget.style.background = P.red; }}
                >
                  Pumunta sa Login
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

                <div>
                  <label style={{ fontSize: 10, fontWeight: 800, color: P.sub, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 7 }}>
                    BAGONG PASSWORD
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPass ? 'text' : 'password'}
                      value={password}
                      onChange={e => { setPassword(e.target.value); setError(''); }}
                      placeholder="••••••••"
                      autoComplete="new-password"
                      required
                      style={inp}
                      onFocus={e => { e.target.style.borderColor = P.red; e.target.style.boxShadow = `0 0 0 3px ${P.redL}`; }}
                      onBlur={e => { e.target.style.borderColor = P.beigeB; e.target.style.boxShadow = 'none'; }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPass(s => !s)}
                      style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: P.muted, padding: '4px', fontFamily: P.font, fontWeight: 600 }}
                    >{showPass ? 'ITAGO' : 'IPAKITA'}</button>
                  </div>
                </div>

                <div>
                  <label style={{ fontSize: 10, fontWeight: 800, color: P.sub, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 7 }}>
                    ULITIN ANG PASSWORD
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showConfirm ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={e => { setConfirmPassword(e.target.value); setError(''); }}
                      placeholder="••••••••"
                      autoComplete="new-password"
                      required
                      style={inp}
                      onFocus={e => { e.target.style.borderColor = P.red; e.target.style.boxShadow = `0 0 0 3px ${P.redL}`; }}
                      onBlur={e => { e.target.style.borderColor = P.beigeB; e.target.style.boxShadow = 'none'; }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(s => !s)}
                      style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: P.muted, padding: '4px', fontFamily: P.font, fontWeight: 600 }}
                    >{showConfirm ? 'ITAGO' : 'IPAKITA'}</button>
                  </div>
                </div>

                {error && (
                  <div style={{ background: P.redL, border: `1px solid ${P.redB}`, padding: '11px 14px', fontSize: 12, color: P.redD, fontWeight: 600 }}>
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  style={{ background: loading ? P.muted : P.red, color: '#fff', border: 'none', padding: '13px 18px', fontSize: 13, fontWeight: 800, cursor: loading ? 'not-allowed' : 'pointer', marginTop: 4, transition: 'background 0.15s', letterSpacing: '0.04em', fontFamily: P.font }}
                  onMouseEnter={e => { if (!loading) e.currentTarget.style.background = P.redD; }}
                  onMouseLeave={e => { if (!loading) e.currentTarget.style.background = loading ? P.muted : P.red; }}
                >
                  {loading ? 'Nagse-save...' : 'I-save ang Bagong Password'}
                </button>

              </form>
            )}

          </div>
        </div>

        {/* Footer */}
        <div style={{ marginTop: 28, textAlign: 'center' }}>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', lineHeight: 1.8 }}>
            F.L.A.R.E. · Flood Level Alert and Risk Evaluation System<br />
            {REGION_FULL} · {new Date().getFullYear()}
          </div>
        </div>

      </div>

      <style>{`
        * { box-sizing: border-box; }
        body { font-family: 'DM Sans', 'Segoe UI', sans-serif; -webkit-font-smoothing: antialiased; margin: 0; }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(28px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        input, button, textarea, select { font-family: 'DM Sans', 'Segoe UI', sans-serif; }
      `}</style>
    </div>
  );
}
